import { useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import type {
  City,
  EventCityMap,
  EventCityMapCreateInput,
  EventCityMapUpdateInput,
  EventProjectMap,
  EventProjectMapCreateInput,
  EventProjectMapUpdateInput,
  Project,
} from '@/lib/types';
import { Button, Checkbox, Combobox, Field, Input, Modal } from '@/components/ui';
import { RETIRE_CONSEQUENCE, keyIsEnterable } from './event-map-rules';
import { subtitleOf, useEventMapTargets } from './use-event-map-targets';

/*
 * Create or edit one mapping.
 *
 * 🔴 **EVERY DECISION IN THIS FILE IS ABOUT ONE FAILURE: A MAPPING THAT POINTS
 * AT THE WRONG RECORD.** It is accepted cleanly, it files submissions against
 * the wrong festival with a 201, and it is indistinguishable afterwards —
 * `eventKey` on the stored answer is audit only and joined on by nothing, so
 * there is no artefact anywhere that disagrees with a wrong row. Nothing
 * downstream can catch this, which is why the whole of the defence is here, at
 * the moment of entering.
 *
 * Four things follow from that and none is decoration:
 *
 *  1. **The project list is COMPLETE or the form says so.** A project missing
 *     because it fell past a page cut-off is indistinguishable from one that
 *     does not exist, and the person who cannot find the 2027 festival picks the
 *     2026 one sitting right where it should have been.
 *  2. **The chosen record's SEASON is rendered under the picker**, because the
 *     trigger shows the label alone and truncates it — so on a chosen row the
 *     one thing that separates two festivals is exactly what falls off the end.
 *  3. **Nothing is pre-selected, ever**, and there is no "none" row. A picker
 *     that opens on a plausible first row is a wrong mapping one distracted save
 *     away.
 *  4. **The two kinds never share a payload builder.** See {@link projectCreate}.
 */

/**
 * Which table, and which row.
 *
 * 🔴 **A DISCRIMINATED UNION, SO A CITY ROW CANNOT BE HANDED TO THE PROJECT ARM
 * AND VICE VERSA.** The two row types are structurally near-identical, so
 * without this a swap at a call site compiles and edits the wrong table — and
 * the request would reach the right endpoint carrying the wrong id, which is a
 * clean 200 onto a mapping nobody meant to touch.
 */
export type EventMapFormTarget =
  | { kind: 'project'; row?: EventProjectMap }
  | { kind: 'city'; row?: EventCityMap };

export interface EventMapFormModalProps {
  open: boolean;
  onClose: () => void;
  target: EventMapFormTarget;
  /** Created or updated. The caller re-reads; nothing is patched in place. */
  onSaved: () => void;
}

/**
 * The form's own value object. **Uniform across both kinds on purpose** — the
 * two differ only in the payload builders at the foot of this file, and
 * everything above them is one set of controls rather than two that drift.
 *
 * ⚠️ **`targetId` is a string because a picker's value is**, and it becomes a
 * number inside those builders and nowhere else.
 */
interface FormValues {
  key: string;
  targetId: string;
  isActive: boolean;
}

type ValueKey = keyof FormValues;

/**
 * The WIRE keys a 422 may name, per kind.
 *
 * ⚠️ **These are the server's spellings, not the form's**, which is the point:
 * `routeFieldErrors` partitions on what the server said, and a list written from
 * the control names would route every real refusal into the banner, marked
 * against nothing, while still counting toward "fields need attention below".
 * {@link CONTROL_FOR_WIRE_KEY} maps them back.
 */
const WIRE_KEYS: Record<EventMapFormTarget['kind'], readonly string[]> = {
  project: ['eventKey', 'projectId', 'isActive'],
  city: ['cityKey', 'cityId', 'isActive'],
};

/** Wire key → the control that renders it. */
const CONTROL_FOR_WIRE_KEY: Record<string, ValueKey> = {
  eventKey: 'key',
  cityKey: 'key',
  projectId: 'targetId',
  cityId: 'targetId',
  isActive: 'isActive',
};

const COPY = {
  project: {
    createTitle: 'Map a festival key',
    editTitle: 'Edit festival mapping',
    keyLabel: 'Festival key from admin-app',
    targetLabel: 'SEED EMERGE project',
    targetPlaceholder: 'Choose the festival this key means…',
    searchPlaceholder: 'Search projects…',
    chooseError: 'Choose the project this key means.',
    listFailed: 'No projects could be read, so there is nothing to map this key to.',
  },
  city: {
    createTitle: 'Map a city key',
    editTitle: 'Edit city mapping',
    keyLabel: 'City key from admin-app',
    targetLabel: 'SEED EMERGE city',
    targetPlaceholder: 'Choose the city this key means…',
    searchPlaceholder: 'Search cities…',
    chooseError: 'Choose the city this key means.',
    listFailed: 'No cities could be read, so there is nothing to map this key to.',
  },
} as const;

export function EventMapFormModal({
  open,
  onClose,
  target,
  onSaved,
}: EventMapFormModalProps): ReactElement | null {
  if (!open) return null;
  /*
   * Remounted per record, so a draft opened on one mapping cannot carry into the
   * next. The KIND is in the key too: the two tables have independent id spaces,
   * so `12` alone would reuse a project form's state for city 12.
   */
  return (
    <EventMapForm
      key={`${target.kind}:${target.row?.id ?? 'new'}`}
      onClose={onClose}
      target={target}
      onSaved={onSaved}
    />
  );
}

function EventMapForm({
  onClose,
  target,
  onSaved,
}: {
  onClose: () => void;
  target: EventMapFormTarget;
  onSaved: () => void;
}) {
  const formId = useId();
  const kind = target.kind;
  const copy = COPY[kind];
  const isEdit = target.row !== undefined;

  const baseline = useMemo<FormValues>(() => {
    const row = target.row;
    if (!row) return { key: '', targetId: '', isActive: true };
    return {
      key: 'eventKey' in row ? row.eventKey : row.cityKey,
      targetId: String('project' in row ? row.project.id : row.city.id),
      isActive: row.isActive,
    };
  }, [target.row]);

  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const targets = useEventMapTargets(kind);

  const changed = useMemo<ValueKey[]>(() => {
    const keys = Object.keys(baseline) as ValueKey[];
    return keys.filter((field) => {
      const next = values[field];
      const previous = baseline[field];
      return typeof next === 'string' && typeof previous === 'string'
        ? next.trim() !== previous.trim()
        : next !== previous;
    });
  }, [values, baseline]);
  const isDirty = changed.length > 0;

  function setField<K extends ValueKey>(field: K, next: FormValues[K]) {
    setValues((prev) => ({ ...prev, [field]: next }));
    setClientErrors((prev) => (field in prev ? without(prev, field) : prev));
    setServerErrors((prev) => (field in prev ? without(prev, field) : prev));
  }

  /**
   * The chosen record in full.
   *
   * ⚠️ **THREE different facts arrive here as `null`** — the list is still
   * arriving, the list read FAILED, and a value the loaded list does not
   * contain. {@link ChosenTarget} tells all three apart rather than printing one
   * sentence for them; the middle one is the dangerous member, because
   * {@link useEventMapTargets} EMPTIES the list on a failed read, so an unread
   * list and a deleted record are byte-identical here.
   */
  const chosen = useMemo(() => {
    const id = Number(values.targetId);
    return Number.isInteger(id) ? (targets.byId.get(id) ?? null) : null;
  }, [targets.byId, values.targetId]);

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (!keyIsEnterable(values.key)) {
      next.key = 'Enter the key exactly as admin-app sends it.';
    }
    if (values.targetId === '') next.targetId = copy.chooseError;
    return next;
  }

  /**
   * Whether an edit has anything to send.
   *
   * ⚠️ **Derived from the PATCH the builders would produce, not from
   * `changed`.** They agree today; deriving it from the real body is what keeps
   * Save honest if a builder ever stops emitting a key — an empty PATCH is a 422
   * and a refusal for "you changed nothing" is a worse answer than a button that
   * was never offered.
   */
  const patchSize = isEdit
    ? Object.keys(kind === 'project' ? projectPatch(values, changed) : cityPatch(values, changed))
        .length
    : 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;

    const clientSide = validate();
    setClientErrors(clientSide);
    if (Object.keys(clientSide).length > 0) {
      setFormError(null);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    setServerErrors({});
    try {
      const row = target.row;
      if (kind === 'project') {
        if (row) await api.updateEventProjectMap(row.id, projectPatch(values, changed));
        else await api.createEventProjectMap(projectCreate(values));
      } else {
        if (row) await api.updateEventCityMap(row.id, cityPatch(values, changed));
        else await api.createEventCityMap(cityCreate(values));
      }
      onSaved();
      onClose();
    } catch (caught) {
      applyFailure(caught);
    } finally {
      setSubmitting(false);
    }
  }

  function applyFailure(caught: unknown) {
    if (!(caught instanceof ApiError)) {
      setFormError('Could not reach the server. Nothing has been saved.');
      return;
    }

    /*
     * ⚠️ **A duplicate key is a 409 and carries NO `details`**, so nothing
     * routes it on its own. It is put on the key field by hand because that is
     * the only field it can be about, and it is the ordinary way this call
     * fails: entering seven keys, somebody enters one twice because nothing on
     * the form tells them they already did. The server's own sentence is
     * rendered rather than one invented here.
     */
    if (caught.status === 409) {
      setServerErrors({ key: caught.message });
      setFormError(null);
      return;
    }

    if (caught.status === 403) {
      setFormError(
        caught.message || 'Your account no longer has permission to change these mappings.',
      );
      return;
    }

    const routed = caught.routeFieldErrors(WIRE_KEYS[kind]);
    const fields: Record<string, string> = {};
    for (const [wireKey, message] of Object.entries(routed.fields)) {
      const control = CONTROL_FOR_WIRE_KEY[wireKey];
      if (control) fields[control] = message;
    }
    setServerErrors(fields);
    /*
     * `unroutable` is everything the server named that no control here renders.
     * It goes in the banner and is deliberately NOT counted toward "fields need
     * attention below", which would point at nothing.
     */
    const banner = [...routed.unroutable, ...caught.formMessages];
    setFormError(banner.length > 0 ? banner.join(' ') : (caught.formMessage ?? caught.message));
  }

  const fieldErrorCount = Object.keys(errors).length;
  /*
   * ⚠️ Save is offerable on create from the start, and on edit only once
   * something has moved. It is NOT disabled while the target list loads: the key
   * can be typed meanwhile, and the client-side check refuses an empty target.
   */
  const saveDisabled = submitting || (isEdit && patchSize === 0);

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? copy.editTitle : copy.createTitle}
      size="md"
      dismissOnBackdrop={!isDirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create mapping'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="grid gap-4">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <Field label={copy.keyLabel} required error={errors.key}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              value={values.key}
              onChange={(e) => setField('key', e.target.value)}
              /*
               * ⚠️ **Spell-check and autocapitalise OFF.** These are machine
               * keys carrying underscores, and a phone keyboard that capitalises
               * the first letter produces a key that is wrong in a way nobody
               * reads back.
               */
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              invalid={Boolean(errors.key)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <div className="grid gap-1.5">
          <Field label={copy.targetLabel} required error={errors.targetId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                value={values.targetId}
                onChange={(next) => setField('targetId', next)}
                options={targets.options}
                loading={targets.loading}
                placeholder={copy.targetPlaceholder}
                searchPlaceholder={copy.searchPlaceholder}
                invalid={Boolean(errors.targetId)}
                /*
                 * ⛔ **NO `emptyOptionLabel`.** A mapping that names nothing is a
                 * row that refuses every submission carrying its key while
                 * looking configured, so "none" is not a choice this form may
                 * offer. Clearing a mapping is Delete, or the tickbox below.
                 */
                {...(targets.footnote ? { footnote: targets.footnote } : {})}
                aria-describedby={describedBy}
              />
            )}
          </Field>

          <ChosenTarget
            chosen={chosen}
            loading={targets.loading}
            lookupFailed={targets.error !== null}
            hasValue={values.targetId !== ''}
          />

          {targets.error && (
            <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
              {copy.listFailed} {targets.error}
            </p>
          )}
        </div>

        {/*
          * ⛔ **THE `description` IS A CONSEQUENCE, NOT HELPER TEXT, AND A SWEEP
          * MUST NOT TAKE IT.** Unticking this closes a public form to a key, and
          * nothing anywhere in SEED EMERGE shows that happening — the whole
          * effect is at admin-app's end. `Checkbox.description` exists for
          * exactly this. See `RETIRE_CONSEQUENCE`.
          */}
        <Checkbox
          checked={values.isActive}
          onChange={(e) => setField('isActive', e.target.checked)}
          label="Accept new submissions naming this key"
          description={RETIRE_CONSEQUENCE}
        />
      </form>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* The payloads — the ONE place a kind becomes a wire key                     */
/* -------------------------------------------------------------------------- */

/**
 * 🔴 **FOUR BUILDERS RATHER THAN ONE PARAMETERISED BY KIND, AND THE DUPLICATION
 * IS THE GUARD.** A single builder returning `Record<string, unknown>` would
 * need a cast at the point of sending — and a cast is exactly what stops `tsc`
 * noticing `cityId` going to the projects endpoint. Written out, each body is
 * checked against the input type its own endpoint declares, so a wrong key is a
 * compile error rather than a 422 in production.
 *
 * ⚠️ **`isActive` is sent on CREATE only when it is not the server's default**,
 * so an ordinary new mapping carries the two keys it is actually about.
 */
function projectCreate(values: FormValues): EventProjectMapCreateInput {
  return {
    eventKey: values.key.trim(),
    projectId: Number(values.targetId),
    ...(values.isActive === false ? { isActive: false } : {}),
  };
}

/**
 * ⛔ **ONLY THE KEYS THAT MOVED.** A PATCH built from a spread of the loaded row
 * would re-assert `eventKey` on a visit that merely retired the mapping — and on
 * a table whose keys are unique, re-sending one is the shape that collides with
 * itself.
 */
function projectPatch(values: FormValues, changed: readonly ValueKey[]): EventProjectMapUpdateInput {
  const patch: EventProjectMapUpdateInput = {};
  if (changed.includes('key')) patch.eventKey = values.key.trim();
  if (changed.includes('targetId')) patch.projectId = Number(values.targetId);
  if (changed.includes('isActive')) patch.isActive = values.isActive;
  return patch;
}

function cityCreate(values: FormValues): EventCityMapCreateInput {
  return {
    cityKey: values.key.trim(),
    cityId: Number(values.targetId),
    ...(values.isActive === false ? { isActive: false } : {}),
  };
}

function cityPatch(values: FormValues, changed: readonly ValueKey[]): EventCityMapUpdateInput {
  const patch: EventCityMapUpdateInput = {};
  if (changed.includes('key')) patch.cityKey = values.key.trim();
  if (changed.includes('targetId')) patch.cityId = Number(values.targetId);
  if (changed.includes('isActive')) patch.isActive = values.isActive;
  return patch;
}

/* -------------------------------------------------------------------------- */
/* The chosen record, rendered in full                                        */
/* -------------------------------------------------------------------------- */

/**
 * What the picker's trigger cannot show.
 *
 * 🔴 **THIS IS THE GUARD AGAINST A WRONG MATCH, NOT A CAPTION ABOUT ONE.** The
 * trigger renders `label` alone and truncates it, so for a project the season —
 * the one thing separating a 2026 festival from its 2027 successor — is exactly
 * what falls off the end. This renders the VALUE, the same standing as a `title`
 * carrying what a cell truncated, and removing it leaves a screen showing a name
 * that two records share.
 *
 * ⚠️ **FOUR states, and only ONE of them is "nothing chosen".** Nothing chosen,
 * still arriving, the list read FAILED, and a value the loaded list genuinely
 * does not contain. That last one is a real and alarming state — the record has
 * been deleted out from under a live mapping — and it must not render as a blank
 * that reads like an empty picker.
 *
 * 🔴 **THE MIDDLE TWO BOTH LEAVE `chosen` NULL AND MEAN OPPOSITE THINGS, WHICH
 * IS WHY `lookupFailed` IS A PARAMETER RATHER THAN A REFINEMENT.**
 * {@link useEventMapTargets} EMPTIES the list on a failed read — deliberately,
 * so nothing can be saved against a list nobody can vouch for — so an unread
 * list and a deleted record are byte-identical by the time they reach here.
 * Without the flag this accuses a live mapping of pointing at a record that does
 * not exist **whenever the list could not be read**, in a `role="alert"` that is
 * announced, and instructs the reader to choose again from a picker that is
 * empty for the very same reason. The plausible next action is to delete the row
 * and re-enter it, which costs every submission naming that key.
 *
 * ⚠️ **The surviving accusation is genuinely REACHABLE here**, unlike its twin
 * on `/b2b-service-maps`: a project or city really can be deleted out from under
 * a live mapping, which is why the sentence exists at all. Suppressing it on a
 * failed read narrows it to the case it was written for; it does not retire it.
 */
function ChosenTarget({
  chosen,
  loading,
  lookupFailed,
  hasValue,
}: {
  chosen: Project | City | null;
  loading: boolean;
  lookupFailed: boolean;
  hasValue: boolean;
}) {
  if (!hasValue) return null;

  if (chosen === null) {
    if (loading) {
      return <p className="m-0 text-xs text-fg-muted">Looking up what this key points at…</p>;
    }
    /*
     * ⚠️ **A read that did not happen is not evidence about a record.** The
     * list's own failure notice renders directly below this and carries the
     * fact, so the only honest output here is nothing — the same ruling
     * `EventMapPanel`'s `TargetSubtitle` already takes for the table.
     */
    if (lookupFailed) return null;
    return (
      <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
        This mapping points at a record that is not in the list. Choose it again before saving.
      </p>
    );
  }

  const subtitle = subtitleOf(chosen);

  return (
    <div className="rounded-md border border-border bg-surface-sunken px-3 py-2">
      <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{chosen.name}</p>
      {/*
        * ⚠️ **Says so plainly when a project carries no cycle, no intake and no
        * dates.** That is a fact about the catalogue a person needs before
        * mapping a key to it — not something to hide behind a blank that reads
        * as "still loading".
        */}
      <p className="mt-0.5 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
        {subtitle || (
          <span className="text-fg-subtle">Nothing recorded to tell it apart from another season</span>
        )}
      </p>
      {!chosen.isActive && (
        <p className="mt-1 mb-0 text-xs text-warning">
          This record is retired. It still resolves for answers already stored.
        </p>
      )}
    </div>
  );
}

/*
 * ⚠️ **The target list lives in `use-event-map-targets.ts`, not here.** The
 * TABLE needs the same records for the same reason this form does — a mapping
 * row carries only `{id, name}`, and a project's name does not identify a season
 * — so one hook serves both and a name reads the same in the picker as it does
 * in the row that picker produced.
 */
